import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@maui/elements'
import { useComponentSize } from 'react-use-size'

import { LinearLineChart, UseRangeSlider, RangeSlider } from '../../../components/common'

export const DetailsTimelineChart = ({
  data: lineData,
  tooltipIcons,
  handlePointClick,
  pointSymbol,
  firstDate,
  lastDate,
  customTooltip,
  withRangeSlider = true,
  ...props
}) => {
  const [values, setValues] = useState([0, 100])
  const [minValue, maxValue] = values
  const { ref, width } = useComponentSize()
  const { getTrackProps, ticks, segments, handles } = UseRangeSlider({
    min: 0,
    max: 100,
    stepSize: 1,
    values,
    onDrag: setValues,
  })

  const twoThirdsWidth = width * 0.66

  const interval = useMemo(() => {
    return Math.round((lastDate - firstDate) / 100)
  }, [firstDate, lastDate])

  const xScaleMin = useMemo(() => {
    return new Date(firstDate.getTime() + interval * minValue).toISOString()
  }, [minValue, firstDate, interval])

  const xScaleMax = useMemo(() => {
    return new Date(firstDate.getTime() + interval * maxValue).toISOString()
  }, [maxValue, firstDate, interval])

  return lineData && (
    <Box
      ref={ref}
      width='100%'
      minWidth="180px"
      height='60px'
      py='10px'
      display="flex"
      flex="1"
      flexDirection="column"
    >
      <LinearLineChart
        width={width}
        displayName={false}
        data={lineData}
        chartProps={{
          isInteractive: true,
          enableGridY: true,
          height: 60,
          lineWidth: 4,
          margin: {
            top: 0,
            right: 30,
            bottom: 25,
            left: 35,
          },
          onClick: handlePointClick,
          pointSymbol,
          enableSlices: false,
          useMesh: true,
          axisBottom: {
            format: '%-m/%-d/%y',
            orient: 'bottom',
            tickSize: 0,
            tickPadding: 10,
            tickValues: 5,
          },
          xScale: {
            type: 'time',
            useUTC: false,
            format: '%Y-%m-%dT%H:%M:%S.%L%Z',
            precision: 'day',
            min: xScaleMin,
            max: xScaleMax
          },
          xFormat: 'time:%Y-%m-%dT%H:%M:%S.%L%Z',
          colors: props.lineColor || '#94000F',
          theme: {
            textColor: props.textColor || 'var(--maui-color-text-deemphasis)',
            axis: {
              ticks: {
                text: {
                  fontSize: 10,
                  lineHeight: 16,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                }
              }
            },
          },
          ...props,
        }}
      />
      {withRangeSlider && <Box display="flex" justifyContent='center' alignItems='center'>
        <Box width={twoThirdsWidth}>
          <RangeSlider
            values={values}
            setValues={setValues}
            getTrackProps={getTrackProps}
            ticks={ticks}
            segments={segments}
            handles={handles} />
        </Box>
      </Box>}
    </Box>
  )
}

DetailsTimelineChart.propTypes = {
  data: PropTypes.array,
  onClick: PropTypes.func,
  tooltipIcons: PropTypes.object,
  handlePointClick: PropTypes.func,
  pointSymbol: PropTypes.func,
  firstDate: PropTypes.instanceOf(Date),
  lastDate: PropTypes.instanceOf(Date),
  customTooltip: PropTypes.func,
  withRangeSlider: PropTypes.bool,
  props: PropTypes.object,
}

export default React.memo(DetailsTimelineChart)
